import React, { useState, Fragment, createElement } from 'react';
import { SearchBar } from '../SearchBar';
import { Button } from '../Button';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon, ClockIcon, AlertCircleIcon, XCircleIcon } from 'lucide-react';
interface ValidationError {
  id: string;
  timestamp: string;
  endpoint: string;
  payload: string;
  errorStack: string[];
  status: 'error' | 'warning';
}
export const ValidationLogsTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'all' | '24h' | '7d' | '30d'>('all');
  // Sample validation errors
  const validationErrors: ValidationError[] = [{
    id: '1',
    timestamp: '2023-11-10T14:30:00Z',
    endpoint: '/transactions/pacs.008',
    payload: '{"transactionId": "123", "amount": -100}',
    errorStack: ['Invalid transaction amount: must be positive', 'Transaction ID format invalid: expected format XX999999', 'Missing required field: currency'],
    status: 'error'
  }, {
    id: '2',
    timestamp: '2023-11-10T13:15:00Z',
    endpoint: '/accounts/acmt.023',
    payload: '{"accountId": "ABC", "balance": "invalid"}',
    errorStack: ['Balance must be a number', 'Account ID must be numeric'],
    status: 'warning'
  }
  // Add more sample errors as needed
  ];
  const filteredErrors = validationErrors.filter(error => error.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) || error.errorStack.some(err => err.toLowerCase().includes(searchTerm.toLowerCase())));
  const exportLogs = () => {
    const csvContent = [['Timestamp', 'Endpoint', 'Error Stack', 'Status'].join(','), ...filteredErrors.map(error => [error.timestamp, error.endpoint, `"${error.errorStack.join('; ')}"`, error.status].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'validation-logs.csv';
    a.click();
  };
  return <div className="space-y-4" data-id="element-1128">
      <div className="flex justify-between items-center mb-6" data-id="element-1129">
        <h2 className="text-xl font-semibold text-gray-900" data-id="element-1130">Validation Logs</h2>
        <Button variant="secondary" onClick={exportLogs} icon={<DownloadIcon size={16} data-id="element-1132" />} data-id="element-1131">
          Export Logs
        </Button>
      </div>
      <div className="flex justify-between items-center mb-4" data-id="element-1133">
        <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="Search endpoints or errors..." data-id="element-1134" />
        <div className="flex space-x-2" data-id="element-1135">
          {['all', '24h', '7d', '30d'].map(period => <button key={period} onClick={() => setTimeFilter(period as any)} className={`px-3 py-1 text-sm rounded-md ${timeFilter === period ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1136">
              {period.toUpperCase()}
            </button>)}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1137">
        <div className="overflow-x-auto" data-id="element-1138">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1139">
            <thead className="bg-gray-50" data-id="element-1140">
              <tr data-id="element-1141">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1142">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1143">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1144">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1145">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1146">
              {filteredErrors.map(error => <Fragment key={error.id} data-id="element-1147">
                  <tr className="hover:bg-gray-50" data-id="element-1148">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1149">
                      <div className="flex items-center" data-id="element-1150">
                        <ClockIcon size={16} className="mr-2 text-gray-400" data-id="element-1151" />
                        {new Date(error.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1152">
                      {error.endpoint}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1153">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${error.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1154">
                        {error.status === 'error' ? <XCircleIcon size={12} className="mr-1" data-id="element-1155" /> : <AlertCircleIcon size={12} className="mr-1" data-id="element-1156" />}
                        {error.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1157">
                      <button onClick={() => setExpandedRow(expandedRow === error.id ? null : error.id)} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-1158">
                        {error.errorStack.length} errors
                        {expandedRow === error.id ? <ChevronUpIcon size={16} className="ml-1" data-id="element-1159" /> : <ChevronDownIcon size={16} className="ml-1" data-id="element-1160" />}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === error.id && <tr data-id="element-1161">
                      <td colSpan={4} className="px-6 py-4 bg-gray-50" data-id="element-1162">
                        <div className="space-y-4" data-id="element-1163">
                          <div className="space-y-2" data-id="element-1164">
                            <h4 className="text-sm font-medium text-gray-900" data-id="element-1165">
                              Error Stack
                            </h4>
                            <ul className="space-y-2" data-id="element-1166">
                              {error.errorStack.map((err, index) => <li key={index} className="flex items-start" data-id="element-1167">
                                  <span className="flex-shrink-0 h-5 w-5 text-red-500" data-id="element-1168">
                                    <XCircleIcon size={20} data-id="element-1169" />
                                  </span>
                                  <span className="ml-2 text-sm text-gray-700" data-id="element-1170">
                                    {err}
                                  </span>
                                </li>)}
                            </ul>
                          </div>
                          <div className="space-y-2" data-id="element-1171">
                            <h4 className="text-sm font-medium text-gray-900" data-id="element-1172">
                              Failed Payload
                            </h4>
                            <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-sm overflow-x-auto" data-id="element-1173">
                              {JSON.stringify(JSON.parse(error.payload), null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};